package com.smashvoter.screens

import androidx.compose.runtime.*
import com.smashvoter.SmashColors
import com.smashvoter.smashGhostButton
import com.smashvoter.smashInput
import com.smashvoter.smashPrimaryButton
import com.smashvoter.api.ApiClient
import com.smashvoter.state.AppState
import kotlinx.coroutines.launch
import org.jetbrains.compose.web.attributes.InputType
import org.jetbrains.compose.web.attributes.placeholder
import org.jetbrains.compose.web.dom.*
import org.jetbrains.compose.web.css.*

@Composable
fun LoginScreen(apiClient: ApiClient) {
    val scope = rememberCoroutineScope()

    var email by remember { mutableStateOf("") }
    var code by remember { mutableStateOf("") }
    var codeSent by remember { mutableStateOf(false) }
    var isLoading by remember { mutableStateOf(false) }
    var errorMessage by remember { mutableStateOf<String?>(null) }

    Div(attrs = {
        style {
            display(DisplayStyle.Flex)
            flexDirection(FlexDirection.Column)
            alignItems(AlignItems.Center)
            justifyContent(JustifyContent.Center)
            minHeight(100.vh)
            backgroundColor(Color(SmashColors.background))
        }
    }) {
        Div(attrs = {
            style {
                backgroundColor(Color(SmashColors.surface))
                padding(40.px)
                borderRadius(16.px)
                property("box-shadow", "0 8px 32px rgba(0,0,0,0.6)")
                border(1.px, LineStyle.Solid, Color(SmashColors.border))
                maxWidth(400.px)
                width(100.percent)
            }
        }) {
            // Logo
            Div(attrs = {
                style {
                    display(DisplayStyle.Flex)
                    justifyContent(JustifyContent.Center)
                    marginBottom(16.px)
                }
            }) {
                Img(src = "smash-logo.svg", attrs = {
                    style {
                        width(72.px)
                        height(72.px)
                    }
                })
            }

            // Title
            H1(attrs = {
                style {
                    textAlign("center")
                    marginTop(0.px)
                    marginBottom(4.px)
                    color(Color(SmashColors.textPrimary))
                    fontSize(32.px)
                    fontWeight("900")
                    letterSpacing((-0.5).px)
                }
            }) { Text("SmashVoter") }

            P(attrs = {
                style {
                    textAlign("center")
                    color(Color(SmashColors.textSecondary))
                    marginTop(0.px)
                    marginBottom(32.px)
                    fontSize(15.px)
                }
            }) { Text("Vote to rank the best") }

            if (!codeSent) {
                // Step 1: request code
                Label(attrs = {
                    style {
                        color(Color(SmashColors.textSecondary))
                        fontSize(13.px)
                        fontWeight("500")
                        letterSpacing(0.3.px)
                        property("text-transform", "uppercase")
                    }
                }) { Text("Email address") }

                Input(type = InputType.Email, attrs = {
                    value(email)
                    placeholder("you@example.com")
                    onInput { email = it.value }
                    onKeyDown { e ->
                        if (e.key == "Enter" && email.isNotBlank() && !isLoading) {
                            isLoading = true
                            errorMessage = null
                            scope.launch {
                                try {
                                    apiClient.requestCode(email)
                                    codeSent = true
                                } catch (_: Throwable) {
                                    errorMessage = "Failed to send code. Please try again. Make sure you use a valid wearetriple.com email."
                                } finally {
                                    isLoading = false
                                }
                            }
                        }
                    }
                    style { smashInput() }
                })

                Button(attrs = {
                    onClick {
                        if (email.isBlank() || isLoading) return@onClick
                        isLoading = true
                        errorMessage = null
                        scope.launch {
                            try {
                                apiClient.requestCode(email)
                                codeSent = true
                            } catch (_: Throwable) {
                                errorMessage = "Failed to send code. Please try again. Make sure you use a valid wearetriple.com email."
                            } finally {
                                isLoading = false
                            }
                        }
                    }
                    style { smashPrimaryButton() }
                }) {
                    Text(if (isLoading) "Sending…" else "Send login code")
                }
            } else {
                // Step 2: verify code
                P(attrs = {
                    style {
                        color(Color(SmashColors.accent))
                        marginBottom(16.px)
                        marginTop(0.px)
                        textAlign("center")
                        fontSize(14.px)
                    }
                }) { Text("Code sent to $email") }

                Label(attrs = {
                    style {
                        color(Color(SmashColors.textSecondary))
                        fontSize(13.px)
                        fontWeight("500")
                        letterSpacing(0.3.px)
                        property("text-transform", "uppercase")
                    }
                }) { Text("8-digit code") }

                Input(type = InputType.Text, attrs = {
                    value(code)
                    placeholder("12345678")
                    onInput { code = it.value }
                    onKeyDown { e ->
                        if (e.key == "Enter" && code.isNotBlank() && !isLoading) {
                            isLoading = true
                            errorMessage = null
                            scope.launch {
                                try {
                                    val result = apiClient.verifyCode(email, code)
                                    AppState.saveSession(result.sessionId, email)
                                } catch (_: Throwable) {
                                    errorMessage = "Invalid or expired code. Please try again."
                                    isLoading = false
                                }
                            }
                        }
                    }
                    style {
                        smashInput()
                        fontSize(24.px)
                        textAlign("center")
                        letterSpacing(8.px)
                    }
                })

                Button(attrs = {
                    onClick {
                        if (code.isBlank() || isLoading) return@onClick
                        isLoading = true
                        errorMessage = null
                        scope.launch {
                            try {
                                val result = apiClient.verifyCode(email, code)
                                AppState.saveSession(result.sessionId, email)
                            } catch (_: Throwable) {
                                errorMessage = "Invalid or expired code. Please try again."
                                isLoading = false
                            }
                        }
                    }
                    style { smashPrimaryButton() }
                }) {
                    Text(if (isLoading) "Verifying…" else "Verify code")
                }

                Button(attrs = {
                    onClick { codeSent = false; code = ""; errorMessage = null }
                    style {
                        smashGhostButton()
                        width(100.percent)
                        marginTop(8.px)
                    }
                }) { Text("Use a different email") }
            }

            errorMessage?.let { msg ->
                P(attrs = {
                    style {
                        color(Color(SmashColors.error))
                        marginTop(12.px)
                        marginBottom(0.px)
                        textAlign("center")
                        fontSize(14.px)
                    }
                }) { Text(msg) }
            }
        }
    }
}
